import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useItemService } from "./useServices";
import { PATH_KEYS } from "@/services/appServices";
import type { LegislationServiceType } from "@/services/legislationServices";
import type {
  StaticListDataSource,
  StaticListItem,
} from "@/models/dashboard/WidgetConfig";
import {
  LEGISLATION_SUMMARY_BATCH_LIMIT,
  type LegislationSummary,
  type LegislationSummaryRequestItem,
} from "@/models/legislation/LegislationSummary";
import type { WorkspaceDocument } from "@/models/workspace/Workspace";
import { getDbValue, getInvalidLabel } from "@/config/datasources";
import { resolveValueFromDbColumn, humanizeWithMapping, renderCellValue } from "@/utils/metadataValueResolver";
import { dateValue } from "@/utils/legislationMetadataUtils";
import {
  parseLegislationOriginalId,
  partitionStaticListItems,
} from "@/pages/dashboard/lib/staticListItems";
import useLegislationFilters from "./useLegislationFilters";
import { logger } from "@/lib/logger";
import { isCancellation } from "@/lib/abort";
import { restError } from "@/models/client/RestResponse";

export interface ResolvedItemInfo {
  title: string;
  type: string;
  status: string;
  isLoading: boolean;
  isError: boolean;
  // For workspace documents
  workspaceId?: string;
  workspaceName?: string;
  updatedAt?: string;
  createdAt?: string;
  // For legislation
  datasource?: string;
  date?: string;
  internalId?: string;
}

/** A legislation item whose stored id parsed, ready to be looked up. */
export interface LegislationLookup {
  originalId: string;
  externalId: string;
  /** The datasource as the item stores it; used in logs. */
  datasource: string;
  /** The stored datasource value the API matches on, or null when unknown. */
  dbValue: string | null;
  itemId: string;
}

/**
 * What each item's originalId resolved to: the document's list fields, or
 * null when it has no latest version or its datasource is unknown.
 */
export type LegislationResolutions = Record<string, LegislationSummary | null>;

const lookupKey = (externalId: string, datasource: string) =>
  `${externalId}|${datasource}`;

/**
 * One lookup per distinct parseable id, sorted so that reordering a list does
 * not change the query key.
 */
export const buildLegislationLookups = (
  sourceItems: StaticListItem[]
): LegislationLookup[] => {
  const byOriginalId = new Map<string, LegislationLookup>();
  sourceItems.forEach((item) => {
    const parsed = parseLegislationOriginalId(item.originalId);
    if (!parsed || byOriginalId.has(item.originalId)) return;
    byOriginalId.set(item.originalId, {
      originalId: item.originalId,
      externalId: parsed.externalId,
      datasource: parsed.datasource,
      dbValue: getDbValue(parsed.datasource),
      itemId: item.id,
    });
  });
  return [...byOriginalId.values()].sort((a, b) =>
    a.originalId.localeCompare(b.originalId)
  );
};

const inBatches = <T>(values: T[], size: number): T[][] => {
  const batches: T[][] = [];
  for (let i = 0; i < values.length; i += size) {
    batches.push(values.slice(i, i + size));
  }
  return batches;
};

/**
 * Resolves every lookup in as few requests as the API's batch limit allows,
 * i.e. one request for any list of up to 100 documents (LAR-580).
 */
export const resolveLegislationLookups = async (
  lookups: LegislationLookup[],
  legislationService: Pick<LegislationServiceType, "resolveSummaries">,
  signal?: AbortSignal
): Promise<LegislationResolutions> => {
  const resolutions: LegislationResolutions = {};
  const requestItems = new Map<string, LegislationSummaryRequestItem>();

  for (const lookup of lookups) {
    if (!lookup.dbValue) {
      // The id parsed, but its datasource is not registered in the config.
      logger.warn(
        "Unknown datasource for static list legislation item — cannot resolve",
        {
          operation: "resolveStaticListItem",
          originalId: lookup.originalId,
          datasource: lookup.datasource,
          itemId: lookup.itemId,
        }
      );
      resolutions[lookup.originalId] = null;
      continue;
    }
    requestItems.set(lookupKey(lookup.externalId, lookup.dbValue), {
      externalId: lookup.externalId,
      datasource: lookup.dbValue,
    });
  }

  if (requestItems.size === 0) return resolutions;

  const found = new Map<string, LegislationSummary>();
  // Items of a batch whose response carried no list; already logged once.
  const unanswered = new Set<string>();
  const batches = inBatches(
    [...requestItems.values()],
    LEGISLATION_SUMMARY_BATCH_LIMIT
  );
  try {
    const responses = await Promise.all(
      batches.map((batch) => legislationService.resolveSummaries(batch, signal))
    );
    responses.forEach((response, index) => {
      if (!response.success) {
        // Throw so React Query applies its retry policy; the catch logs it.
        throw restError(
          response,
          "Failed to resolve legislation documents for static list"
        );
      }
      // The type says a list, but a body that did not parse reads as null.
      const value: unknown = response.value;
      if (!Array.isArray(value)) {
        logger.warn(
          "Legislation summaries response is not a list — treating it as no results",
          {
            operation: "resolveStaticListItems",
            itemCount: batches[index].length,
            valueType: value === null ? "null" : typeof value,
          }
        );
        batches[index].forEach((item) =>
          unanswered.add(lookupKey(item.externalId, item.datasource))
        );
        return;
      }
      for (const entry of response.value) {
        if (entry.legislation) {
          found.set(
            lookupKey(entry.externalId, entry.datasource),
            entry.legislation
          );
        }
      }
    });
  } catch (error) {
    // A cancelled query (the user left the dashboard mid-load) is not a
    // failure; rethrow it untouched so React Query settles it as cancelled.
    if (!isCancellation(error, signal)) {
      logger.error("Error resolving legislation for static list items", {
        operation: "resolveStaticListItems",
        error,
        itemCount: requestItems.size,
      });
    }
    throw error;
  }

  for (const lookup of lookups) {
    if (!lookup.dbValue) continue;

    const key = lookupKey(lookup.externalId, lookup.dbValue);
    const legislation = found.get(key);
    if (!legislation && !unanswered.has(key)) {
      logger.warn("Failed to resolve legislation document for static list", {
        operation: "resolveStaticListItem",
        originalId: lookup.originalId,
        externalId: lookup.externalId,
        datasource: lookup.datasource,
        itemId: lookup.itemId,
      });
    }
    resolutions[lookup.originalId] = legislation ?? null;
  }

  return resolutions;
};

/**
 * Hook to resolve document details for static list items.
 *
 * A list can hold legislation items, workspace documents, or — when the widget
 * runs on the "mixed" dataSource — both at once, so the two resolution paths
 * run side by side and each item is looked up on the path its own type names.
 */
export const useStaticListItemResolver = (
  items: StaticListItem[],
  dataSource: StaticListDataSource
) => {
  const legislationService = useItemService((services) => services.legislation);
  const workspaceService = useItemService((services) => services.workspace);
  const { useGetFilterConfiguration } = useLegislationFilters();
  const { data: filterConfig } = useGetFilterConfiguration();

  const { sourceItems, workspaceDocumentItems } = partitionStaticListItems(
    items,
    dataSource
  );

  const lookups = buildLegislationLookups(sourceItems);

  // One request for every legislation item in the list. A query per item used
  // to fetch each whole document, html included, and a dashboard's static
  // lists fanned out into enough parallel calls to exhaust the API's
  // connection pool (LAR-580).
  const legislationQuery = useQuery({
    queryKey: [
      PATH_KEYS.legislation,
      "resolve-summaries",
      lookups.map((lookup) => lookup.originalId),
    ],
    queryFn: ({ signal }) =>
      resolveLegislationLookups(lookups, legislationService, signal),
    enabled: lookups.length > 0,
    staleTime: 5 * 60 * 1000,
    // Adding or removing an item changes the key; keep the rows that are
    // already resolved on screen while the new list loads.
    placeholderData: keepPreviousData,
  });

  // Single query to fetch all workspaces (only when needed)
  const workspacesQuery = useQuery({
    queryKey: [PATH_KEYS.workspace, "all-for-resolve"],
    queryFn: async () => {
      try {
        const response = await workspaceService.getAllWorkspaces();
        if (response.success) {
          return response.value;
        }
        // Throw so React Query applies retry: 2; the catch block below logs it.
        throw restError(
          response,
          "Failed to fetch workspaces for static list resolution"
        );
      } catch (error) {
        logger.error("Error fetching workspaces for static list resolution", {
          operation: "resolveStaticListWorkspaces",
          error,
        });
        throw error instanceof Error
          ? error
          : new Error("Error fetching workspaces for static list resolution");
      }
    },
    enabled: workspaceDocumentItems.length > 0,
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });

  // Helper to find a document in a workspace (recursively in folders)
  const findDocumentInWorkspace = (
    workspace: { documents: WorkspaceDocument[]; folders: { documents: WorkspaceDocument[]; folders: unknown[] }[] },
    documentId: string
  ): WorkspaceDocument | null => {
    // Check root documents
    const rootDoc = workspace.documents.find((d) => d.id === documentId);
    if (rootDoc) return rootDoc;

    // Check folders recursively
    const searchFolders = (folders: { documents: WorkspaceDocument[]; folders: unknown[] }[]): WorkspaceDocument | null => {
      for (const folder of folders) {
        const doc = folder.documents.find((d) => d.id === documentId);
        if (doc) return doc;

        const nested = searchFolders(folder.folders as typeof folders);
        if (nested) return nested;
      }
      return null;
    };

    return searchFolders(workspace.folders);
  };

  // Extract type from legislation metadata using filter config
  const extractLegislationType = (legislation: LegislationSummary): string => {
    const datasource = legislation.datasource?.toLowerCase();
    const sourceConfig = filterConfig?.sources?.[datasource];
    const documentTypeFilter = sourceConfig?.filters?.["documentType"];

    if (documentTypeFilter) {
      const raw = resolveValueFromDbColumn(legislation, {
        key: "documentType",
        databaseColumn: documentTypeFilter.databaseColumn,
        valueMapping: documentTypeFilter.valueMapping,
      });
      const humanized = humanizeWithMapping(raw, documentTypeFilter.valueMapping);
      if (humanized !== null && humanized !== undefined) {
        const str = renderCellValue(humanized);
        return str.length > 0 ? str.charAt(0).toUpperCase() + str.slice(1) : "-";
      }
    }

    // Fallback
    const raw = resolveValueFromDbColumn(legislation, {
      key: "documentType",
      databaseColumn: null,
    });
    if (raw && typeof raw === "string") {
      return raw.charAt(0).toUpperCase() + raw.slice(1);
    }

    return "-";
  };

  // Build resolved info map
  const resolvedItems = new Map<string, ResolvedItemInfo>();

  // Iterate items so every item gets an entry every render.
  const legislationInFlight = legislationQuery.isFetching;
  sourceItems.forEach((item) => {
    // A malformed id is never looked up, so it would otherwise report neither
    // loading nor error and the row would sit blank forever.
    if (!parseLegislationOriginalId(item.originalId)) {
      logger.warn(
        "Malformed legislation id for static list item — cannot resolve",
        {
          operation: "resolveStaticListItem",
          originalId: item.originalId,
          itemId: item.id,
        }
      );
      resolvedItems.set(item.id, {
        title: "",
        type: "-",
        status: "-",
        isLoading: false,
        isError: true,
        datasource: "-",
        date: "-",
      });
      return;
    }

    // `null` is a settled "could not resolve"; `undefined` means the item is
    // not in the (possibly placeholder) data yet.
    const resolution = legislationQuery.data?.[item.originalId];

    if (resolution) {
      const status =
        resolution.isCurrent === null || resolution.isCurrent === undefined
          ? "-"
          : resolution.isCurrent
            ? "Gældende"
            : getInvalidLabel(resolution.datasource);

      resolvedItems.set(item.id, {
        title: resolution.title,
        type: extractLegislationType(resolution),
        status,
        isLoading: false,
        isError: false,
        datasource: resolution.datasource || "-",
        date: resolution.publishedOn
          ? dateValue(resolution.publishedOn) || "-"
          : "-",
        internalId: resolution.internalId,
      });
    } else {
      // Leave `title` empty on hard failure so consumers can detect the
      // unresolved state via `isError && !title`. Callers that want a
      // user-visible fallback should compose it themselves, e.g.
      // `item.customName || resolved.title || item.originalId`.
      resolvedItems.set(item.id, {
        title: "",
        type: "-",
        status: "-",
        isLoading: legislationInFlight,
        isError:
          !legislationInFlight &&
          (legislationQuery.isError || resolution === null),
        datasource: "-",
        date: "-",
      });
    }
  });

  // Resolve workspace documents from the single workspaces query
  const workspaces = workspacesQuery.data || [];

  workspaceDocumentItems.forEach((item) => {
    // Find the document and its workspace
    let foundDoc: WorkspaceDocument | null = null;
    let foundWorkspaceId: string | undefined;
    let foundWorkspaceName: string | undefined;

    for (const workspace of workspaces) {
      const doc = findDocumentInWorkspace(workspace, item.originalId);
      if (doc) {
        foundDoc = doc;
        foundWorkspaceId = workspace.id;
        foundWorkspaceName = workspace.name;
        break;
      }
    }

    if (foundDoc) {
      const activeRevision = foundDoc.revisions?.[foundDoc.activeRevision - 1];
      const updatedAt = activeRevision?.updatedAt
        ? dateValue(new Date(activeRevision.updatedAt).toISOString()) || "-"
        : "-";
      const createdAt = foundDoc.createdAt
        ? (dateValue(foundDoc.createdAt) || "-")
        : "-";

      resolvedItems.set(item.id, {
        title: foundDoc.name,
        type: foundDoc.documentType || "Dokument",
        status: activeRevision?.isIndexed ? "Indekseret" : "Ikke indekseret",
        isLoading: workspacesQuery.isLoading,
        isError: workspacesQuery.isError,
        workspaceId: foundWorkspaceId || item.workspaceId,
        workspaceName: foundWorkspaceName || item.workspaceName || "-",
        updatedAt,
        createdAt,
      });
    } else {
      if (!workspacesQuery.isLoading && workspaces.length > 0) {
        logger.warn("Workspace document not found in static list resolution", {
          operation: "resolveStaticListWorkspaceDocument",
          documentId: item.originalId,
          itemId: item.id,
          workspaceId: item.workspaceId,
        });
      }
      // Keep `title` empty so the form can render a real "couldn't load"
      // state. isError covers both: the query failed (workspacesQuery.isError)
      // or the query succeeded but this document wasn't found in any workspace
      // (!workspacesQuery.isLoading && !workspacesQuery.isError).
      resolvedItems.set(item.id, {
        title: "",
        type: "-",
        status: "-",
        isLoading: workspacesQuery.isLoading,
        isError: !workspacesQuery.isLoading,
        workspaceId: item.workspaceId,
        workspaceName: item.workspaceName || "-",
        updatedAt: "-",
        createdAt: "-",
      });
    }
  });

  // Check if any query is loading
  const isLoading =
    legislationQuery.isLoading ||
    (workspaceDocumentItems.length > 0 && workspacesQuery.isLoading);

  return {
    resolvedItems,
    isLoading,
    getResolvedInfo: (itemId: string): ResolvedItemInfo => {
      return resolvedItems.get(itemId) || {
        title: "-",
        type: "-",
        status: "-",
        // Default to loading rather than error: the item id may appear one
        // render before its query is registered, and we don't want a flash of
        // the destructive error block.
        isLoading: true,
        isError: false,
      };
    },
  };
};

export default useStaticListItemResolver;
